import { ReactNode, useEffect, useMemo } from 'react';

import { GetDocumentsByAssetResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import listVector from '@/assets/vectors/list.svg';
import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useModulesStore } from '@/stores/modules.store';

import { columns } from './table-meta';

export function DocumentsTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  onRowClick,
  sorting,
  onSortingChange,
}: {
  notFoundDisplay: ReactNode;
  data: GetDocumentsByAssetResponseDTO['data'];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  onRowClick: (row: GetDocumentsByAssetResponseDTO['data'][number]) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
}) {
  const { syncModuleColumns } = useModulesStore();

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.AssetDocuments, columns);
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<GetDocumentsByAssetResponseDTO['data'][number]>
        data={data}
        columns={columns}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        initialSorting={initialSorting}
        onRowClick={onRowClick}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
      />
    </div>
  );
}

export function NoDocumentsCreatedDisplay({ openDocumentDrawer }: { openDocumentDrawer: () => void }) {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={listVector} alt="List" className="h-[170px] w-[250px]" />
      <span className="text-xl leading-[30px] font-medium">No documents have been created yet</span>
      <div className="pointer-events-auto flex gap-1">
        <Button variant="success" onClick={openDocumentDrawer}>
          Create Document
        </Button>
      </div>
    </div>
  );
}

export function NoDocumentsFoundFromSearchDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No documents match your search</span>
    </div>
  );
}

export function NoDocumentsFoundFromFiltersDisplay() {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No documents match your filters</span>
    </div>
  );
}
